/**
 * Карта прогулки: метки, слежение, служебные слои Leaflet.
 * Общие для прогулки по маршруту редакции и по своему маршруту
 */
import { useEffect, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { Check, ArrowRight } from '@phosphor-icons/react';
import { WALK_MAP } from '../../constants/colors';

// Leaflet собирает divIcon из строки HTML, поэтому иконки Phosphor
// отрисовываются в разметку заранее — свои значки вместо символов ✓ и ▸
export const iconMarkup = (Icon, size, color) =>
  renderToStaticMarkup(<Icon size={size} color={color} weight="bold" />);

// Название места уходит в HTML метки строкой — экранируем: оно из CMS
export const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

// Подпись-название рядом с пином. Шрифт и цвета — литералами, divIcon
// живёт вне React
export const pinLabel = (title, dark) => `<div style="position:absolute;left:38px;top:5px;max-width:180px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;padding:3px 8px;border-radius:6px;font:600 13px/18px 'Wix Madefor Text',system-ui,sans-serif;${dark
  ? 'background:#16181D;color:#FFFFFF;'
  : `background:#FFFFFF;color:#16181D;border:1px solid #E3E6EB;`}">${escapeHtml(title)}</div>`;

/**
 * Метки мест. Иерархия видна до чтения номеров:
 * - `target` — куда идти сейчас: синий пин с номером и названием;
 * - `viewed` — место, открытое тапом для просмотра: тёмный пин;
 * - `upcoming` — впереди: белый круг с тёмным номером, спокойнее цели;
 * - `done` / `skipped` — позади: маленькие серые.
 *
 * Раньше у текущего места был синий ореол — так в картах рисуют самого
 * пользователя, и роли путались. Пройденные метки были самыми яркими,
 * а предстоящие — чёрными: взгляд цеплялся за то, что уже позади
 */
// Иконки кешируются по виду: страница перерисовывается каждую секунду
// (таймер), и новая иконка на каждый кадр пересоздавала бы DOM меток —
// ореол пользователя начинал пульсацию заново раз в секунду
export const iconCache = new Map();
export const cachedIcon = (key, make) => {
  if (!iconCache.has(key)) iconCache.set(key, make());
  return iconCache.get(key);
};

export const createStopIcon = (index, kind, title) =>
  cachedIcon(`stop|${index}|${kind}|${title || ''}`, () => makeStopIcon(index, kind, title));

export const makeStopIcon = (index, kind, title) => {
  const num = index + 1;
  if (kind === 'target' || kind === 'viewed') {
    const fill = kind === 'target' ? WALK_MAP.ahead : WALK_MAP.upcomingText;
    return L.divIcon({
      className: '',
      html: `<div style="position:relative;width:34px;height:44px;">
        <svg width="34" height="44" viewBox="0 0 34 44" style="display:block"><path d="M17 42.5S32.5 27.6 32.5 17A15.5 15.5 0 0 0 1.5 17C1.5 27.6 17 42.5 17 42.5Z" fill="${fill}" stroke="#FFFFFF" stroke-width="2.5"/></svg>
        <span style="position:absolute;left:0;right:0;top:8px;text-align:center;color:#FFFFFF;font:700 14px/18px 'Wix Madefor Text',system-ui,sans-serif;">${num}</span>
        ${title ? pinLabel(title, kind === 'viewed') : ''}
      </div>`,
      iconSize: [34, 44],
      iconAnchor: [17, 43],
    });
  }
  if (kind === 'done' || kind === 'skipped') {
    const done = kind === 'done';
    return L.divIcon({
      className: '',
      html: `<div style="width:22px;height:22px;border-radius:50%;box-sizing:border-box;display:flex;align-items:center;justify-content:center;${done
        ? `background:${WALK_MAP.passed};border:2px solid #FFFFFF;`
        : `background:#FFFFFF;border:2px solid ${WALK_MAP.passed};`}">${iconMarkup(done ? Check : ArrowRight, 12, done ? '#FFFFFF' : WALK_MAP.passed)}</div>`,
      iconSize: [22, 22],
      iconAnchor: [11, 11],
    });
  }
  return L.divIcon({
    className: '',
    html: `<div style="width:26px;height:26px;border-radius:50%;box-sizing:border-box;background:#FFFFFF;border:2px solid ${WALK_MAP.upcomingBorder};display:flex;align-items:center;justify-content:center;color:${WALK_MAP.upcomingText};font:700 12px/1 'Wix Madefor Text',system-ui,sans-serif;">${num}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
  });
};

/**
 * Пользователь: точка с расходящимся ореолом и, если известно направление,
 * конус взгляда. Направление округляем до 10° — иконка пересоздаётся
 * только при заметном повороте
 */
/**
 * Станция метро — старт пути, пока нет геолокации: синий круг с «М»,
 * как знак метро в интерфейсе, и подпись с названием
 */
export const createMetroIcon = (name) => cachedIcon(`metro|${name}`, () => L.divIcon({
  className: '',
  html: `<div style="position:relative;width:26px;height:26px;">
    <div style="width:26px;height:26px;border-radius:50%;box-sizing:border-box;background:${WALK_MAP.ahead};border:2px solid #FFFFFF;display:flex;align-items:center;justify-content:center;color:#FFFFFF;font:800 13px/1 'Wix Madefor Display',system-ui,sans-serif;">М</div>
    <div style="position:absolute;left:32px;top:2px;white-space:nowrap;padding:2px 8px;border-radius:6px;background:#FFFFFF;color:#16181D;border:1px solid #E3E6EB;font:600 13px/18px 'Wix Madefor Text',system-ui,sans-serif;">${escapeHtml(name)}</div>
  </div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
}));

/**
 * Пользователь — точка с расходящимся ореолом.
 *
 * Конуса «куда смотрю» нет намеренно. Браузер и Telegram отдают курс
 * движения, а не сторону, куда повёрнут телефон: стоя на месте или
 * медленно идя, он показывал случайное направление — проверено
 * на телефоне. Компаса в Telegram Mini App нет
 */
export const createUserIcon = () => cachedIcon('user', () => L.divIcon({
  className: '',
  html: `<div style="position:relative;width:14px;height:14px;">
      <span class="map-user-halo"></span>
      <span style="position:absolute;inset:-2px;border-radius:50%;background:${WALK_MAP.user};border:3px solid #FFFFFF;box-shadow:0 1px 4px rgba(0,0,0,.25)"></span>
    </div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
}));

/**
 * Атрибуция: убираем приставку «Leaflet» с флагом, которую клиент вставляет
 * сам, и переносим её в левый верхний угол. ODbL требует указания авторства
 * данных — оно остаётся в attribution у TileLayer; ссылка на библиотеку
 * к лицензии отношения не имеет.
 *
 * Снизу справа надпись налезала на кнопки карты, сверху её теперь
 * закрывает полоса прогресса — поэтому левый нижний угол.
 */
export const AttributionPrefix = () => {
  const map = useMap();
  useEffect(() => {
    map.attributionControl?.setPrefix(false);
    map.attributionControl?.setPosition('bottomleft');
  }, [map]);
  return null;
};

/** Карта меняет высоту вместе с шапкой панели — Leaflet надо известить */
export const MapResizer = ({ height }) => {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize({ pan: false });
  }, [map, height]);
  return null;
};

// Масштаб слежения: пешеходный, видно улицу и соседние дома
export const FOLLOW_ZOOM = 16;

// Линии маршрута (брендбук 2.11): «сейчас» — яркая сплошная с белой
// обводкой, «впереди» — точки, «позади» — серая тонкая
export const LINE_BASE = { lineCap: 'round', lineJoin: 'round', opacity: 1 };
export const GUIDE_STYLE = { ...LINE_BASE, color: WALK_MAP.guide, weight: 6 };
export const CASING_STYLE = { ...LINE_BASE, color: WALK_MAP.casing, weight: 10 };
// Штрих нулевой длины с круглым концом рисуется точкой
export const AHEAD_STYLE = { ...LINE_BASE, color: WALK_MAP.ahead, weight: 5, dashArray: '0.1 10' };
export const PASSED_STYLE = { ...LINE_BASE, color: WALK_MAP.passed, weight: 3 };

// Дальше этого цель и пользователя в один кадр не сводим: масштаб
// откатился бы до района, и улиц стало бы не разобрать
export const FRAME_BOTH_MAX_M = 1500;
// Самый крупный масштаб, когда в кадре и пользователь, и цель
export const FRAME_MAX_ZOOM = 17;
// Отступы кадра по бокам: справа — кнопки карты
export const FRAME_PAD_LEFT = 40;
export const FRAME_PAD_RIGHT = 72;
export const FRAME_PAD_BOTTOM = 32;

/**
 * Карта едет за пользователем и держит в кадре цель — место, к которому
 * он идёт. Раньше в центре был только пользователь, и цель оказывалась
 * у нижней кромки под кнопками или за краем экрана.
 *
 * Чтобы карта не дёргалась на каждом обновлении позиции, кадр
 * перестраивается, только когда кто-то из двоих вышел из видимой
 * области или масштаб разошёлся с нужным на целый шаг. По мере
 * приближения карта сама крупнеет.
 *
 * Цель дальше FRAME_BOTH_MAX_M в кадр не тянем — иначе масштаб
 * откатывался до района и это выглядело как поломка (так уже было).
 * Тогда, как раньше, в центре один пользователь.
 *
 * Слежение снимается, как только человек сам подвинул или приблизил
 * карту: вырывать её из-под рук нельзя. Возвращает кнопка.
 * `padTop` — высота плашки прогресса над картой.
 */
export const MapFollower = ({ userPosition, target, follow, onUserPan, padTop }) => {
  const map = useMap();

  // Жесты человека: сдвиг, щипок, колесо. Программные setView и fitBounds
  // этих событий не поднимают
  useEffect(() => {
    const handle = () => onUserPan();
    const container = map.getContainer();
    const onTouch = (e) => { if (e.touches?.length > 1) onUserPan(); };
    map.on('dragstart', handle);
    container.addEventListener('touchstart', onTouch, { passive: true });
    container.addEventListener('wheel', handle, { passive: true });
    return () => {
      map.off('dragstart', handle);
      container.removeEventListener('touchstart', onTouch);
      container.removeEventListener('wheel', handle);
    };
  }, [map, onUserPan]);

  // Первое включение слежения приближает до пешеходного масштаба
  const zoomedRef = useRef(false);
  useEffect(() => {
    if (!follow) { zoomedRef.current = false; }
  }, [follow]);

  useEffect(() => {
    if (!follow) return;
    const user = Array.isArray(userPosition) ? userPosition : null;
    const goal = Array.isArray(target) ? target : null;
    if (!user && !goal) return;

    // Без пары или слишком далеко — в центре кто есть, как раньше
    if (!user || !goal || map.distance(user, goal) > FRAME_BOTH_MAX_M) {
      const zoom = zoomedRef.current
        ? map.getZoom()
        : Math.max(map.getZoom(), FOLLOW_ZOOM);
      zoomedRef.current = true;
      map.setView(user || goal, zoom, { animate: true });
      return;
    }

    const bounds = L.latLngBounds([user, goal]);
    const size = map.getSize();
    const want = Math.min(
      FRAME_MAX_ZOOM,
      map.getBoundsZoom(bounds, false, L.point(FRAME_PAD_LEFT + FRAME_PAD_RIGHT, padTop + FRAME_PAD_BOTTOM))
    );
    const inView = [user, goal].every((ll) => {
      const p = map.latLngToContainerPoint(ll);
      return p.x >= FRAME_PAD_LEFT && p.x <= size.x - FRAME_PAD_RIGHT
        && p.y >= padTop && p.y <= size.y - FRAME_PAD_BOTTOM;
    });
    if (zoomedRef.current && inView && Math.abs(want - map.getZoom()) < 1) return;

    zoomedRef.current = true;
    map.fitBounds(bounds, {
      paddingTopLeft: [FRAME_PAD_LEFT, padTop],
      paddingBottomRight: [FRAME_PAD_RIGHT, FRAME_PAD_BOTTOM],
      maxZoom: FRAME_MAX_ZOOM,
      animate: true,
    });
  }, [map, follow, padTop, userPosition?.[0], userPosition?.[1], target?.[0], target?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

